import Component from '../../js/core/component.js';
import prepend from '../../js/utilities/prepend/u-prepend.js';
// import keycodes from '../../js/utilities/keycodes/u-keycodes.js';
import convertToButton from '../../js/utilities/convert-to-button/u-convert-to-button.js';
import randomId from '../../js/utilities/random-id/u-random-id.js';
import focusableElements from '../../js/utilities/focusable-elements/u-focusable-elements.js';
import '../../js/vendor/polyfills/polyfill-before.js';

import icon from '../icon/c-icon.js';

/* globals google */

/**
 * Pop out Class
 */
class PopOut extends Component {

	/**
	 * Default settings for this component
	 * @return {object} Default settings
	 */
	_defaultSettings () {
		return {
			debug: false,
			button: null,
			popOut: null,
			classNames: {
				isOpen: 'is-open',
				popOut: 'c-pop-out',
				toggleButton: 'c-additional-navigation__button',
				closeButton: 'c-pop-out__close'
			},
			text: {
				toggleButton: 'Öppna',
				closeButton: 'Stäng'
			}
		};
	}

	/**
	 * Initialize Pop out
	 */
	_init () {
		this.toggleButton = this.settings.button;
		this.popOut = this.settings.popOut;

		this.isCreated = false;
		this.googleTranslateInit = false;

		this.init();
	}

	_createContent () {
		if (!this.settings.popOut) {
			this.popOut = document.createElement('div');
			this.popOut.classList.add(this.settings.classNames.popOut);
			this.toggleButton.after(this.popOut);
		}

		this._createCloseButton();

		if(!this.popOut.id) {
			randomId(this.popOut);
		}
		this.toggleButton.setAttribute('aria-controls', this.popOut.id);
		this.popOut.setAttribute('aria-labelledby', this.toggleButton.id);
		this.popOut.setAttribute('tabindex', '-1');

		this.isCreated = true;
	}

	_createToggleButton () {
		if (!this.toggleButton) {
			const toggleButtonContent  = this.popOut.getAttribute('data-btn-text') || this.settings.text.toggleButton;

			this.toggleButton = document.createElement('button');

			this.toggleButton.appendChild(document.createTextNode(toggleButtonContent));
			this.toggleButton.classList.add(this.settings.classNames.toggleButton);
			this.toggleButton.setAttribute('type', 'button');
			this.popOut.before(this.toggleButton);
		}

		if (this.toggleButton.tagName === 'A') {
			this.toggleButton = convertToButton(this.toggleButton);
		}

		this.toggleButton.setAttribute('aria-haspopup', 'true');
		this.toggleButton.setAttribute('aria-expanded', 'false');

		if (!this.toggleButton.id){
			randomId(this.toggleButton);
		}

		this.toggleButton.popOut = this;
	}

	_createCloseButton () {
		const closeIcon = icon({
			icon: '/gui/images/icons/cross.svg'
		});
		const hiddenText = document.createElement('span');

		hiddenText.classList.add('t-visually-hidden');
		hiddenText.appendChild(document.createTextNode(this.settings.text.closeButton));

		this.closeButton = document.createElement('button');

		this.closeButton.appendChild(closeIcon);
		closeIcon.classList.add('c-icon--xs');
		this.closeButton.appendChild(hiddenText);
		this.closeButton.classList.add(this.settings.classNames.closeButton);

		prepend(this.popOut, this.closeButton);

		this.closeButton.addEventListener('click', this.close.bind(this));
	}

	_getFocusableElements (target) {
		return target.querySelectorAll(focusableElements);
	}

	_focusHandler (event) {
		if (!this.popOut.contains(event.target) && event.target !== this.toggleButton) {
			this.close();
		}
	}

	_clickHandler (event) {
		if (!this.popOut.contains(event.target) && event.target !== this.toggleButton) {
			this.close();
		}
	}

	_initGoogleTranslate () {
		if(typeof google !== 'undefined' && typeof google.translate.TranslateElement !== 'undefined' && this.popOut.querySelector('#google_translate_element') && !this.googleTranslateInit) {
			new google.translate.TranslateElement({pageLanguage: 'sv'}, 'google_translate_element');
			this.googleTranslateInit = true;
		}
	}

	/* =============================================================================
	 * Public methods
	============================================================================= */

	init () {
		if (!this.toggleButton && !this.popOut) {
			return;
		}

		this._createToggleButton();

		if (this.popOut) {
			this.popOut.popOut = null;
		}

		this.toggleButton.addEventListener('click', this.toggle.bind(this));
	}

	toggle () {
		if (this.isOpen()) {
			this.close();
		}
		else {
			this.open();
		}
	}

	open () {
		if (!this.isCreated) {
			this._createContent();
		}
		this.popOut.classList.add(this.settings.classNames.isOpen);
		this.toggleButton.setAttribute('aria-expanded', 'true');
		this.toggleButton.classList.add('js-pop-out-arrow');
		this.popOut.focus();

		// Handle click and focus outside of popOut
		this.focusableElements = this._getFocusableElements(document);
		this.focusHandler = this._focusHandler.bind(this);
		this.clickHandler = this._clickHandler.bind(this);
		for (let element of this.focusableElements) {
			element.addEventListener('focus', this.focusHandler);
		}
		this.toggleButton.addEventListener('focus', this.focusHandler);
		document.addEventListener('click', this.clickHandler);

		this._initGoogleTranslate();
	}

	close () {
		for (let element of this.focusableElements) {
			element.removeEventListener('focus', this.focusHandler);
		}
		document.removeEventListener('click', this.clickHandler);

		this.popOut.classList.remove(this.settings.classNames.isOpen);
		this.toggleButton.setAttribute('aria-expanded', 'false');
		this.toggleButton.classList.remove('js-pop-out-arrow');
		this.toggleButton.focus();
	}

	isOpen () {
		return this.popOut && this.popOut.classList.contains(this.settings.classNames.isOpen);
	}

	destroy () {
		// Remove buttons
		this.toggleButton.remove();
		if(this.closeButton) {
			this.closeButton.remove();
		}

		// Remove attributes
		this.popOut.classList.remove(this.settings.classNames.isOpen);
		this.popOut.removeAttribute('aria-labelledby', this.toggleButton.id);
		this.popOut.removeAttribute('tabindex', '-1');

		// Remove eventlisteners
		document.removeEventListener('click', this.clickHandler);
		if(this.focusableElements) {
			for (let element of this.focusableElements) {
				element.removeEventListener('focus', this.focusHandler);
			}
		}

		this._initGoogleTranslate();
	}
}
document.addEventListener('newcontent', (e) => {
	e.target.querySelectorAll('[data-js-pop-out-btn]').forEach((element) => {
		element.popOut = new PopOut({
			debug: false,
			button: element
		});
	});

	e.target.querySelectorAll('.c-pop-out:not(.c-additional-navigation__translate)').forEach((element) => {
		element.popOut = new PopOut({
			debug: false,
			popOut: element
		});
	});
});

export default PopOut;


// WEBPACK FOOTER //
// ./components/pop-out/c-pop-out.js